// src/lib/floorScore.js
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. Tyler: "sales manager should have some sort of
//               leaderboard of the sales floor team that he can see ... we need
//               to come up with a point scale system."
//
// A SCOREBOARD FOR THE THINGS THAT ARE NORMALLY INVISIBLE.
//
// Every dealership on earth already has a leaderboard and it ranks units sold.
// Jazz says twice in his own material not to do that:
//
//   weekly rhythm:  "Recognition of process behaviors, not only deliveries"
//   the one on one: "Recognize process behaviors, not only deliveries"
//
// Nobody scores process because process is invisible. A manager cannot see who
// rehearsed, who asked for a sign off, who taught somebody else. This product
// can see all of it, and that is the whole reason a points system here is worth
// anything rather than being a gimmick.
//
// So: no units, no gross, no closing ratio. Five behaviours, every one of them
// a choice somebody made about getting better at the job.
//
// WHO SEES WHAT, AND THIS IS THE LOAD BEARING DECISION.
//
// THE MANAGER sees the board. They are accountable for the floor and already
// see progress, and Tyler asked for exactly this.
//
// THE FLOOR SEES THEIR OWN SCORE AND NOT THE RANKING. Two reasons, and the
// second is the serious one.
//
//   The charter says a salesperson does not see anybody else's performance,
//   and `floorMetrics.js` already says a leaderboard shown to the floor "is a
//   leaderboard with extra steps".
//
//   And a public ranking of VOLUNTARY behaviours gets gamed within a week.
//   Practice is the one thing here nobody is made to do, which is exactly what
//   makes it an honest signal, and it stops being honest the moment somebody
//   can climb a table by running forty junk drills.
//
// WHY IT IS A WEEK.
//
// His coaching rhythm is weekly. A running lifetime total means whoever was
// hired first wins forever, which tells a manager nothing about this week and
// tells a new hire not to bother.
//
// EVERYTHING HERE IS PURE. See CLAUDE.md on the import rule.

// The weights, and the reason for each one, because a points system nobody
// understands feels arbitrary and arbitrary is demotivating. The `why` is shown
// in the product, not buried here.
export const SCORING = [
  {
    key: 'signoff',
    points: 25,
    cap: null,
    label: 'Signed off on a step',
    why: 'A manager watched them do it and approved it. The hardest thing on this list to get.',
  },
  {
    key: 'take',
    points: 15,
    cap: 2,
    label: 'Taught the floor',
    why: 'Recorded how they actually run a step, for everybody else. Nobody is made to do this.',
  },
  {
    key: 'check',
    points: 10,
    cap: null,
    label: 'Passed a knowledge check',
    why: 'Every question right. It is not possible to pass one twice, so this cannot be farmed.',
  },
  {
    key: 'kept',
    points: 8,
    cap: null,
    label: 'Kept a commitment',
    why: 'They wrote down what they would do differently on the next customer, and then did it.',
  },
  {
    key: 'drill',
    points: 4,
    cap: 5,
    label: 'Practised',
    why: 'A rep in the coach. Capped at five a week, because the twentieth drill is not four times the fifth.',
  },
]

export const WEEK_DAYS = 7

const BY_KEY = Object.fromEntries(SCORING.map((s) => [s.key, s]))

// One person's week. `counts` is raw occurrences per key; the cap is applied
// here rather than at the query, so the UI can honestly show "6 drills, 5
// counted" instead of quietly dropping one.
export function scorePerson(counts) {
  const lines = []
  let total = 0
  for (const rule of SCORING) {
    const raw = counts?.[rule.key] || 0
    if (raw === 0) continue
    const counted = rule.cap ? Math.min(raw, rule.cap) : raw
    const points = counted * rule.points
    total += points
    lines.push({ key: rule.key, label: rule.label, raw, counted, points, capped: counted < raw })
  }
  return { total, lines }
}

// THE BOARD, AND IT HAS NO LAST PLACE.
//
// Anybody who did something is ranked. Anybody who did nothing is returned
// separately and is NOT given a position, because "you are eighth of eight"
// tells a person nothing they can act on and is the single most reliable way to
// make somebody stop opening an app.
//
// A manager still sees exactly who they are. The difference is that the screen
// reads as a list of people to go and talk to rather than a podium with a
// basement.
export function buildBoard(members, countsByUser) {
  const scored = []
  const quiet = []
  for (const m of members || []) {
    const { total, lines } = scorePerson(countsByUser?.[m.id])
    if (total > 0) scored.push({ person: m, total, lines })
    else quiet.push({ person: m })
  }
  scored.sort((a, b) => b.total - a.total || String(a.person.id).localeCompare(String(b.person.id)))
  // Shared rank on a tie. Two people on 40 are both second.
  let last = null
  let rank = 0
  scored.forEach((row, i) => {
    if (row.total !== last) {
      rank = i + 1
      last = row.total
    }
    row.rank = rank
  })
  return { scored, quiet, floorTotal: scored.reduce((a, r) => a + r.total, 0) }
}

// Turn the five raw result sets into per user counts. Kept separate from the
// queries so the shaping is testable without a database.
export function tally({ signoffs, takes, checks, commitments, drills }) {
  const out = {}
  const bump = (id, key) => {
    if (!id) return
    out[id] = out[id] || {}
    out[id][key] = (out[id][key] || 0) + 1
  }
  for (const r of signoffs || []) if (r.status === 'approved') bump(r.user_id, 'signoff')
  for (const r of takes || []) if (r.status === 'published') bump(r.author_id, 'take')
  for (const r of checks || []) if (r.passed) bump(r.user_id, 'check')
  for (const r of commitments || []) if (r.status === 'kept') bump(r.user_id, 'kept')
  for (const r of drills || []) bump(r.user_id, 'drill')
  return out
}

// What a manager should actually do about the board, in one line. A ranking with
// no next action is a scoreboard, and his method is about inspection.
export function boardHeadline({ scored, quiet }) {
  if (scored.length === 0 && quiet.length === 0) return 'Nobody on the floor yet.'
  if (scored.length === 0) return 'Nothing recorded on the floor this week.'
  if (quiet.length === 1) return `${scored.length} putting the work in, and one person you have not heard from.`
  if (quiet.length > 1) return `${scored.length} putting the work in, and ${quiet.length} you have not heard from.`
  return 'Everybody on the floor did something this week.'
}

export { BY_KEY }
